import type { Policy } from "@shared/schema";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { policyTermLabel } from "./asset-helpers";

interface Props {
  policies: Policy[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Container test id (e.g. "vehicle-dialog-policy-picker"). */
  testId: string;
  /** Builds the per-checkbox test id from a policy id. */
  checkboxTestId: (policyId: string) => string;
  error?: string;
}

// Renewal terms share a number and carrier, so the term dates and status are
// what tell the options apart (CSR-2335).
function policyLabel(p: Policy): string {
  const status = p.status ? ` · ${p.status}` : "";
  return `${policyTermLabel(p)} (${p.carrier})${status}`;
}

/**
 * CSR-1148 — multi-select list of the insured's policies, used by the asset
 * dialogs so one asset can be linked to several policies at once. The set of
 * checked policies IS the set of policies the asset is linked to (checking
 * adds, unchecking removes).
 */
export function PolicyMultiSelect({
  policies,
  selected,
  onChange,
  testId,
  checkboxTestId,
  error,
}: Props) {
  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id],
    );
  };

  return (
    <div data-testid={testId}>
      <Label>Related Policies</Label>
      <p className="text-xs text-muted-foreground mb-2">
        Link this asset to one or more policies.
      </p>
      <div
        className={`space-y-2 rounded-md border p-3 ${error ? "border-destructive" : ""}`}
      >
        {policies.map((p) => (
          <label
            key={p.id}
            className="flex items-center gap-2 cursor-pointer text-sm"
          >
            <Checkbox
              checked={selected.includes(p.id)}
              onCheckedChange={() => toggle(p.id)}
              data-testid={checkboxTestId(p.id)}
              className="cursor-pointer"
            />
            <span>{policyLabel(p)}</span>
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
